import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, forwardRef, Input, Optional, Output, SkipSelf } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { SHARED_DIRECTIVES } from '../../utils/shared-directives';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';

@Component({
  standalone: true,
  selector: 'app-generic-datetimepicker',
  templateUrl: './generic-datetimepicker.component.html',
  styleUrl: './generic-datetimepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule,
    FlexLayoutModule,
    MatIconModule, 
    SHARED_DIRECTIVES, 
    DatePickerModule, 
    FloatLabelModule, 
    ReactiveFormsModule, 
    FormsModule],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GenericDateTimePickerComponent),
    multi: true
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => GenericDateTimePickerComponent),
    multi: true
  }],
})
export class GenericDateTimePickerComponent implements ControlValueAccessor, Validator {

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() formControlName: string = '';
  @Input() width: string = '100%';
  @Input() isStartDate: boolean = false;
  @Input() isEndDate: boolean = false;

  @Input()
  set minDate(value: Date | string | null | undefined) {
    this._minDate = this.parseDate(value);
  }

  get minDate(): Date | null {
    return this._minDate;
  }

  @Input()
  set maxDate(value: Date | string | null | undefined) {
    this._maxDate = this.parseDate(value);
  }

  get maxDate(): Date | null {
    return this._maxDate;
  }

  @Output() dateChanged = new EventEmitter<Date>();

  private _minDate: Date | null = null;
  private _maxDate: Date | null = null;

  value: Date | null = null;

  constructor(@Optional() @SkipSelf() private controlContainer: ControlContainer,
    private el: ElementRef) { }

  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onValidatorChange: () => void = () => { };


  onChange = (value: any) => { };
  onTouched = () => { };

  writeValue(value: any): void {
    if (!value) {
      this.value = null;
      return;
    }

    this.value = this.parseDate(value) ?? null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: any) {
    this.value = event.target.value;
    this.onChange(this.value);
  }

  onCalendarChange(value: Date) {
    this.value = value;
    this.onChange(this.value);
    this.dateChanged.emit(value);
  }

  aplicarMascara(event: any) {
    let valor = event.target.value;

    valor = valor.replace(/\D/g, "");

    if (valor.length > 2) {
      valor = valor.replace(/^(\d{2})(\d)/, "$1/$2");
    }
    if (valor.length > 5) {
      valor = valor.replace(/^(\d{2})\/(\d{2})(\d)/, "$1/$2/$3");
    }
    if (valor.length > 10) {
      valor = valor.substring(0, 10);
    }

    event.target.value = valor;
    if (valor.length === 10) {
      const [dia, mes, ano] = valor.split('/');
      const data = new Date(+ano, +mes - 1, +dia);

      this.value = data;
      this.onChange(this.value);
    } else {
      this.value = null;
      this.onChange(this.value);
    }
  }

  get control(): AbstractControl | null {
    if (!this.controlContainer) return null;
    return this.controlContainer.control?.get(this.formControlName) ?? null;
  }

  get isInvalid(): boolean {
    const ctrl = this.control;
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  parseDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (match) {
      const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
      return new Date(+year, +month - 1, +day, +hour, +minute, +second);
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
